<template>
  <div class="weekly-menu-view">
    <div v-if="isAppReady">
      <el-alert
        v-if="showDueDateAlert"
        :title="dueDateMessage"
        :close-text="$t('deadlineTimeBanner.dismissButton')"
      />
      <el-row>
        <el-col
          :cols="{ offset: 0, span: 12 }"
          :md="{ offset: 0, span: 3 }"
          :lg="{ offset: 1, span: 3 }"
          class="menu-city-container"
        >
          <el-row>
            <p class="menu-city-title" data-test-selector="menu-city-title">
              Your are choosing your menu in
            </p>
          </el-row>
          <el-row>
            <div class="menu-city-tag" data-test-selector="menu-city-tag">
              <common-tag color="var(--warning-base)">{{ officeName }} </common-tag>
            </div>
          </el-row>
        </el-col>
        <el-col :offset="0" :span="19">
          <el-row>
            <div class="title">
              <week-navigator
                :next-path="previousWeekPath"
                :is-navigator-visible="previousWeekIsVisible"
                base-path="/menu"
                previous-week
              />
              <h1>{{ calendarTitle }}</h1>
              <week-navigator
                :next-path="nextWeekPath"
                :is-navigator-visible="nextWeekIsVisible"
                base-path="/menu"
              />
            </div>
          </el-row>
          <el-row>
            <checkbox-week-disable
              v-if="canUserEdit"
              :update-checkboxes="updateWeekCheckboxes"
              class="hidden-xs-only"
              @alert-destructive="alertDestructive"
              @reset-update-checkboxes="resetUpdateWeekCheckboxes"
            />
          </el-row>
        </el-col>
      </el-row>
      <el-row :gutter="40">
        <el-col :md="{ span: 20, offset: 0 }" :lg="{ span: 19, offset: 1 }">
          <the-calendar
            :can-user-edit="canUserEdit"
            :update-checkboxes="updateDailyCheckboxes"
            @alert-destructive="alertDestructive"
            @reset-update-checkboxes="resetUpdateDailyCheckboxes"
          />
        </el-col>
        <el-col :md="{ span: 4 }" :lg="{ span: 4 }" class="hidden-sm-and-down">
          <el-row>
            <el-col>
              <el-card v-if="canUserEdit" shadow="none">
                <checkbox-group
                  :display-description="false"
                  :title="$t('settings.dietTypeHeader')"
                  :disabled="!canUserEdit"
                />
              </el-card>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-card v-if="canUserEdit && officeHasNotesLink" shadow="none">
                <h3 class="card-title">
                  {{ $t('settings.fillInvite.header') }}
                </h3>
                <common-button
                  :primary="!isDueDate"
                  :disabled="!canUserEdit"
                  @click="openNotesLink"
                >
                  {{ $t('settings.fillInvite.button') }}
                </common-button>
              </el-card>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <div class="save-button">
            <common-button
              :primary="!isDueDate"
              :disabled="!canUserEdit"
              :accesskey="accessKey"
              @click="checkSavedMeals()"
            >
              {{ $t('calendar.saveButton') }}
            </common-button>
          </div>
        </el-col>
      </el-row>
      <modal-response
        :visible="isModalVisible"
        :success="modal.success"
        :title="modal.title"
        :message="modal.message"
        :show-buttons="modal.showButtons"
        @confirm-checked="updateCalendarStatus"
        @reverse-checked="restoreCheckbox"
        @close="updateVisibility"
      />
    </div>
    <feedback-footer :version="apiVersion" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import get from 'lodash.get';
import parser from 'cron-parser';
import { MESSAGE_TYPES, DAY_LIST, NAVIGATION_ACCESS_KEYS } from '@/constants';
import { getWeekStartAndEndDate, parseWeek } from '@/helpers';
import TheCalendar from '@/components/weeklymenu/TheCalendar';
import CheckboxWeekDisable from '@/components/CheckboxWeekDisable';
import CheckboxGroup from '@/components/CheckboxGroup';
import WeekNavigator from '@/components/WeekNavigator';
import FeedbackFooter from '@/components/FeedbackFooter';
import ModalResponse from '@/components/ModalResponse';
import CommonButton from '@/components/common/CommonButton';
import CommonTag from '@/components/common/CommonTag';

export default {
  name: 'WeeklyMenuPage',
  components: {
    CheckboxWeekDisable,
    CheckboxGroup,
    TheCalendar,
    FeedbackFooter,
    WeekNavigator,
    ModalResponse,
    CommonButton,
    CommonTag,
  },
  data() {
    return {
      isModalVisible: false,
      modal: {
        success: false,
        message: '',
        title: '',
        showButtons: false,
      },
      eventName: '',
      updateWeekCheckboxes: false,
      updateDailyCheckboxes: false,
      date: '',
      accessKey: NAVIGATION_ACCESS_KEYS.menu.sendOrder,
    };
  },
  computed: {
    ...mapState('global', {
      globalMessage: (state) => state.message,
      apiVersion: (state) => state.apiVersion,
    }),
    ...mapState('orders', {
      wereOrdersUpdated: (state) => state.wereOrdersUpdated,
    }),
    ...mapState('weeklyMenu', {
      isDueDate: (state) => state.isDueDate,
      weekNumber: (state) => state.weekNumber,
      selectedWeek: (state) => state.selectedWeek,
      requestMenuFailure: (state) => state.requestMenuFailure,
      week: (state) => state.week,
    }),
    ...mapState('user', {
      userOffice: (state) => state.user.officeInfo,
      user: (state) => state.user,
      isRequestingUser: (state) => state.isRequestingUser,
      requestUserFailure: (state) => state.requestUserFailure,
    }),
    ...mapState('events', { mealEvents: (state) => state.mealEvents }),
    isAppReady() {
      return !this.requestUserFailure && !this.requestMenuFailure;
    },
    calendarTitle() {
      const weekDates = getWeekStartAndEndDate(this.weekNumber);
      return `
        ${weekDates.startDate.format('MMMM DD')}
        ${this.$t('calendar.to')}
        ${weekDates.endDate.format('MMMM DD')}
      `;
    },

    canUserEdit() {
      return this.weekNumber > 0 && !this.isDueDate;
    },

    showDueDateAlert() {
      return this.weekNumber === 1 && this.isDueDate;
    },

    dueDateMessage() {
      const closeDateCron = get(this.userOffice, 'closeDate', '');
      const startDateCron = get(this.userOffice, 'startDate', '');
      const closeDateString = parser
        .parseExpression(closeDateCron)
        .prev()
        .toISOString();
      const startDateString = parser
        .parseExpression(startDateCron)
        .prev()
        .toISOString();

      const noDaylightSavingsTemporalOffset = '-07:00';
      const dueDay = moment(closeDateString)
        .utcOffset(noDaylightSavingsTemporalOffset)
        .format('dddd');
      const dueHour = moment(closeDateString)
        .utcOffset(noDaylightSavingsTemporalOffset)
        .format('hh:mm a');
      const startDay = moment(startDateString)
        .utcOffset(noDaylightSavingsTemporalOffset)
        .format('dddd');
      const startHour = moment(startDateString)
        .utcOffset(noDaylightSavingsTemporalOffset)
        .format('hh:mm a');

      return this.$t('deadlineTimeBanner.message', { dueDay, dueHour, startDay, startHour });
    },
    previousWeekPath() {
      return this.getNextPath(true);
    },
    nextWeekPath() {
      return this.getNextPath(false);
    },
    previousWeekIsVisible() {
      return this.isNavigatorVisible(true);
    },
    nextWeekIsVisible() {
      return this.isNavigatorVisible(false);
    },
    officeHasNotesLink() {
      return get(this.userOffice, 'notesLink');
    },
    officeName() {
      return this.userOffice ? this.userOffice.name : '';
    },
  },
  watch: {
    globalMessage() {
      if (this.globalMessage.type !== MESSAGE_TYPES.MODAL) {
        this.$message({
          type: this.globalMessage.type,
          message: this.$t(this.globalMessage.message),
        });
      } else {
        this.modal.success = this.wereOrdersUpdated;
        this.modal.title = this.$t(this.globalMessage.title);
        this.modal.message = this.$t(this.globalMessage.message);
        this.isModalVisible = true;
      }
    },
    weekNumber() {
      this.init();
    },
    requestUserFailure(newRequestUserFailureValue) {
      if (newRequestUserFailureValue) {
        this.displayErrorMessage({
          message: 'welcome.messages.requestUserFailure',
        });
      }
    },
    requestMenuFailure(newRequestMenuFailure) {
      if (newRequestMenuFailure) {
        this.displayErrorMessage({
          message: 'weeklyMenuPage.messages.menuFetchError',
        });
      }
    },
    userOffice() {
      const weekNumber = this.weekNumber;
      this.initializeWeekMenu({ weekNumber });
    },
  },
  created() {
    this.initializeEvents();
    this.init();
    this.getAPIVersion();
  },
  methods: {
    ...mapActions('events', { initializeEvents: 'initializeEvents' }),
    ...mapActions('orders', {
      initializeWeekOrdersByUser: 'initializeWeekOrdersByUser',
      validateUserOrdersSync: 'validateUserOrdersSync',
      removeWeekOrdersByEvent: 'removeWeekOrdersByEvent',
      removeOrdersByDay: 'removeOrdersByDay',
      removeWeekOrders: 'removeWeekOrders',
    }),
    ...mapActions('weeklyMenu', {
      initializeWeekMenu: 'initializeWeekMenu',
    }),
    ...mapActions('global', {
      displayErrorMessage: 'displayErrorMessage',
      getAPIVersion: 'getAPIVersion',
    }),
    alertDestructive(eventName, checked, date) {
      if (checked) {
        this.isModalVisible = true;
        this.modal.showButtons = true;
        this.modal.success = false;
        this.eventName = eventName;
        this.date = date;
        this.modal.title =
          eventName !== 'all'
            ? this.$t('calendar.noMealWarnings.event', { event: eventName })
            : this.$t('calendar.noMealWarnings.all');
        this.modal.message = this.$t('calendar.noMealWarnings.message');
      }
    },
    updateCalendarStatus() {
      if (this.eventName !== 'all') {
        if (DAY_LIST.includes(this.eventName)) {
          this.removeDayOrders(this.eventName, this.date);
        } else {
          const eraseEventMeals = this.mealEvents.find(
            (mealEvent) => mealEvent.name === this.eventName
          );
          this.removeWeekOrdersByEvent({
            mealEvent: eraseEventMeals.id,
            startDate: this.week.startDate,
            endDate: this.week.endDate,
            office: this.userOffice.id,
          });
        }
      } else {
        this.removeWeekOrders({
          startDate: this.week.startDate,
          endDate: this.week.endDate,
        });
      }
    },
    removeDayOrders(dayName, date) {
      if (this.canUserEdit) {
        this.removeOrdersByDay({
          date,
          office: this.user.office,
          startDate: this.week.startDate,
          endDate: this.week.endDate,
        });
      } else {
        this.displayErrorMessage({
          message: 'calendar.messages.removeMealDayFail',
        });
      }
    },
    resetUpdateWeekCheckboxes() {
      this.updateWeekCheckboxes = false;
    },
    resetUpdateDailyCheckboxes() {
      this.updateDailyCheckboxes = false;
    },
    restoreCheckbox() {
      this.updateWeekCheckboxes = true;
      this.updateDailyCheckboxes = true;
    },
    init() {
      const weekNumber = this.weekNumber;
      this.initializeWeekOrdersByUser({ weekNumber });
      this.initializeWeekMenu({ weekNumber });
    },
    checkSavedMeals() {
      this.validateUserOrdersSync(this.weekNumber);
    },
    updateVisibility() {
      this.isModalVisible = false;
      this.modal.showButtons = false;
    },
    getNextPath(isPreviousWeek) {
      const selectedWeekMoment = parseWeek(this.selectedWeek);
      const currentWeekMoment = parseWeek(moment());
      if (isPreviousWeek) {
        if (selectedWeekMoment.isAfter(currentWeekMoment)) {
          return '';
        }
        return 'previous';
      }
      if (selectedWeekMoment.isBefore(currentWeekMoment)) {
        return '';
      }
      return 'next';
    },
    isNavigatorVisible(isPreviousWeek) {
      const selectedWeekMoment = parseWeek(this.selectedWeek);
      const currentWeekMoment = parseWeek(moment());
      if (isPreviousWeek) {
        if (selectedWeekMoment.isBefore(currentWeekMoment)) {
          return false;
        }
        return true;
      }
      if (selectedWeekMoment.isAfter(currentWeekMoment)) {
        return false;
      }
      return true;
    },
    openNotesLink() {
      const notesLink = get(this.userOffice, 'notesLink', '');
      window.open(notesLink, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../scss/variables';

.weekly-menu-view {
  .el-row:nth-child(2) {
    margin-bottom: 16px;
    margin-top: 4px;
  }
  .title {
    font-size: $title-font-size;
    font-weight: $bold-font;
    width: 420px;
    display: flex;
    justify-content: space-between;
  }

  .menu-city-container {
    width: 205px;
  }

  @media only screen and (max-width: 550px) {
    .title {
      width: 100%; // adjust size to fit mobile
      font-size: 1.2em;
    }
    .menu-city-container {
      width: 100%;
    }
  }

  .menu-city-title {
    margin-bottom: 10px;
    text-align: center;
    font-size: 12px;
  }

  .menu-city-tag {
    text-align: center;
    font-size: 12px;
  }

  .el-card {
    border: $table-border-color;
    border-radius: 8px;
    padding: 12px 24px;
  }

  .el-card__body {
    padding: 0;
  }

  .el-checkbox__inner {
    width: 16px;
    height: 16px;

    &::after {
      height: 8px;
      left: 5px;
    }
  }

  .save-button {
    display: flex;
    justify-content: center;
    padding: 15px 0;
    margin-bottom: 60px;
  }

  .card-title {
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
  }
}

/deep/ .el-alert {
  position: fixed;
  top: 60px;
  left: 0;
  z-index: 10;
  height: 160px;
  background-color: $sunshade-orange-color;

  &__content {
    padding: 0 40px;
  }

  &__title {
    margin-right: 264px;
    font-size: 20px;
    color: white;
  }

  &__closebtn {
    font-weight: bold;
    margin-top: 100px;
    margin-right: 50px;
  }

  &__closebtn.is-customed {
    font-size: 20px;
    color: white;
  }
}

@media only screen and (max-width: 550px) {
  /deep/ .el-alert {
    height: auto;
    &__content {
      padding: 0 0 40px;
    }
    &__closebtn {
      position: absolute;
      top: auto;
      bottom: 20px;
      right: 50px;
      margin: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/WeeklyMenuPage.vue